import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Papel = 'super_admin' | 'admin_tenant' | 'usuario';

/**
 * O backend devolve as permissões já resolvidas em /api/me — o frontend nunca
 * recalcula quem pode o quê a partir do papel. É o que evita a divergência
 * clássica entre a guarda da rota e o item do menu.
 */
export type AuthUser = {
  id: number;
  nome: string;
  email: string;
  papel: Papel;
  tenant_id: number | null;
  tenant: string | null;
  permissoes: string[];
  modulos_ativos: string[];
  foto_url: string | null;
  /** Senha provisória: só a tela de troca abre até a pessoa definir a dela. */
  trocar_senha: boolean;
};

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: AuthUser) => void;
  atualizarUsuario: (user: AuthUser) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      setAuth: (token, user) => set({ token, user, isAuthenticated: true }),
      atualizarUsuario: (user) => set({ user }),
      clearAuth: () => set({ token: null, user: null, isAuthenticated: false }),
    }),
    { name: 'condusvale-auth' },
  ),
);

/**
 * Pergunta única sobre permissão — nunca comparar `papel` na tela.
 *
 * O `?? []` não é preciosismo: este store é persistido no localStorage, e o
 * navegador de quem já usou uma versão anterior devolve o usuário no formato
 * daquela versão. Um `user` sem `permissoes` derrubaria a tela inteira, e o
 * `?.` sozinho só protege contra o usuário ausente, não contra o campo.
 */
export function usePode(slug: string): boolean {
  return useAuthStore((estado) => (estado.user?.permissoes ?? []).includes(slug));
}

export function useTemModulo(slug: string): boolean {
  return useAuthStore((estado) => (estado.user?.modulos_ativos ?? []).includes(slug));
}
